//src/common/data/handlerepeaterdata.js 
import {
  fetchRepeaters,
  createRepeater,
  updateRepeater,
  deleteRepeater,
  clearRepeaterAssignment,
  createSignalBooster,
  fetchRepeaterHistoryDataStandAlone,
  fetchRepeaterHistoryData,
  fetchSignalBoosterHistoryData,
  fetchSignalBoosterHistoryDataStandAlone
} from './repeaters.api.js';


export const updateRepeaters = async (unit, repeatersToUpdate) => {

  if (!repeatersToUpdate || repeatersToUpdate.length === 0) {
    return {
      success: true,
      processed: []
    };
  }

  const normaliseId = (value) => {
    if (!value) return '';

    if (typeof value === 'object') {
      return String(
        value.$oid ||
        value._id ||
        value.Id ||
        value.id ||
        ''
      ).trim();
    }

    return String(value).trim();
  };

  const samePosition = (a, b) => {
    return (
      a?.config?.side === b?.config?.side &&
      String(a?.config?.axle) === String(b?.config?.axle)
    );
  };

  const duplicateRepeaterIds = repeatersToUpdate
    .map(rep => String(rep.Id || '').trim())
    .filter(Boolean)
    .filter((id, index, arr) => arr.indexOf(id) !== index);

  if (duplicateRepeaterIds.length > 0) {
    throw new Error(
      `The same Location Sensor Id was entered more than once: ${[...new Set(duplicateRepeaterIds)].join(', ')}`
    );
  }

  const currentUnitId = normaliseId(unit.Id || unit.unitId);
  const existingRepeaters = await fetchRepeaters();

  const processed = [];

  for (const repeater of repeatersToUpdate) {
    try {
      if (repeater.unitId == null) {
        repeater.unitId = unit.Id || unit.unitId;
      }

      const targetUnitId = normaliseId(repeater.unitId);

      const existingRepeater = existingRepeaters.find(r =>
        String(r.Id) === String(repeater.Id)
      );

      if (existingRepeater) {
        const existingUnitId = normaliseId(existingRepeater.unitId);

        const belongsToAnotherUnit =
          existingUnitId &&
          targetUnitId &&
          existingUnitId !== targetUnitId;

        if (belongsToAnotherUnit) {
          throw new Error(
            `Location Sensor ${repeater.Id} is already assigned to another unit and cannot be moved.`
          );
        }
      }

      const previousRepeaterInSamePosition = existingRepeaters.find(r => {
        const existingUnitId = normaliseId(r.unitId);

        return (
          existingUnitId === currentUnitId &&
          String(r.Id) !== String(repeater.Id) &&
          samePosition(r, repeater)
        );
      });

      if (previousRepeaterInSamePosition) {
        await clearRepeaterAssignment(unit.customerId, previousRepeaterInSamePosition.Id);

        processed.push({
          repeaterId: previousRepeaterInSamePosition.Id,
          action: 'cleared-old-position'
        });
      }

      if (existingRepeater) {
        await updateRepeater(unit.customerId, repeater);

        processed.push({
          repeaterId: repeater.Id,
          action: 'updated'
        });

      } else {
        await createRepeater(unit.customerId, repeater);

        processed.push({
          repeaterId: repeater.Id,
          action: 'created'
        });
      }

    } catch (error) {
      console.error(
        `Error processing repeater ${repeater.Id || "unknown"}:`,
        error
      );

      throw error;
    }
  }

  return {
    success: true,
    processed
  };
};

export const deleteRepeaters = async (customerId, repeaters) => {
  for (const repeater of repeaters) {
    try {
      await deleteRepeater(customerId, repeater.Id);
    } catch (error) {
      console.error(`Failed to delete repeater with ID ${repeater.Id}:`, error);
    }
  }
};

export const createSignalBoosters = async (unit, signalBoosters) => {
  const failedBoosters = [];

  for (let s = 0; s < signalBoosters.length; s++) {
    const signalBooster = signalBoosters[s];

    try {
      await createSignalBooster(unit.customerId, signalBooster);
    } catch (error) {
      // Log the error and track the failed booster
      console.error(`Error processing signal booster with ID ${signalBooster.unitId || "unknown"}:`, error);
      failedBoosters.push(signalBooster);
    }
  }

  // Optionally handle or report the failedBoosters after the loop
  if (failedBoosters.length > 0) {
    console.warn(`The following signal boosters failed to process:`, failedBoosters);
  }
};

export const getRepeaterHistoryData = async (unit, repeater) => {

  const repeaterLocation = typeof repeater === 'object' && repeater !== null ? repeater?.location : repeater;
  const results = unit.unitType === 'TRAILER'
    ? await fetchRepeaterHistoryDataStandAlone(unit.unitId, repeaterLocation)
    : await fetchRepeaterHistoryData(unit.imei, repeaterLocation);

  return await new Promise((resolve, reject) => {
    if (results.data.length) {

      const data = results.data.map(r => ({
        imei: unit.imei,
        repeaterId: r.repeaterId,
        unixTime: new Date(r.payloadTS).getTime() / 1000,
        time: r.payloadTS,
        voltage: Math.round((r.voltage / 1000) * 10) / 10
      }));

      if (data.length) {
        data.sort((a, b) => a.unixTime - b.unixTime);
        resolve(data);
      } else {
        reject(new Error(`No repeater data.`));
      }
    } else {
      reject(new Error(`No results from fetchRepeaterHistoryData`));
    }
  }).catch(alert);
};

// export const getSignalBoosterHistoryData = async (unit, booster) => {

//     const results = unit.unitType === 'TRAILER'
//         ? await fetchSignalBoosterHistoryDataStandAlone(unit.unitId)
//         : await fetchSignalBoosterHistoryData(unit.imei);

//     return await new Promise((resolve, reject) => {
//         if (results.data.length) {

//             const data = results.data.map(r => ({
//                 imei: unit.imei,
//                 repeaterId: r.repeaterId,
//                 unixTime: new Date(r.payloadTS).getTime() / 1000,
//                 time: r.payloadTS,
//                 voltage: Math.round((r.voltage / 1000) * 10) / 10
//             }));

//             if (data.length) {
//                 data.sort((a, b) => a.unixTime - b.unixTime);
//                 resolve(data);
//             } else {
//                 reject(new Error(`No repeater data.`));
//             }
//         } else {
//             reject(new Error(`No results from fetchRepeaterHistoryData`));
//         }
//     }).catch(alert);
// }

export const getSignalBoosterHistoryData = async (unit, booster) => {

  const results = unit.unitType === 'TRAILER'
    ? await fetchSignalBoosterHistoryDataStandAlone(unit.unitId)
    : await fetchSignalBoosterHistoryData(unit.imei);

  return await new Promise((resolve, reject) => {

    if (!results.data || !results.data.length) {
      return reject(new Error("No results from getSignalBoosterHistoryData"));
    }

    // ⭐ FILTER FOR THIS SPECIFIC BOOSTER ONLY
    const filtered = results.data.filter(r => r.signalBoosterId === booster.Id);

    if (!filtered.length) {
      return reject(new Error(`No history for booster ${booster.Id}`));
    }

    // ⭐ MAP TO CHART FORMAT
    const data = filtered.map(r => ({
      imei: unit.imei,
      repeaterId: booster.Id,
      unixTime: new Date(r.payloadTS).getTime() / 1000,
      time: r.payloadTS,
      voltage: Math.round((r.voltage / 1000) * 10) / 10
    }));

    // ⭐ SORT ASCENDING
    data.sort((a, b) => a.unixTime - b.unixTime);

    resolve(data);

  }).catch((err) => {
    console.error(err);
    alert(err.message);
  });
}



